import axios from 'axios';
import { API_URL, NEW_API_URL } from '../app/constants/APIKEYS';

// 디버그 플래그: 자동 로그아웃 비활성화 (환경변수로 제어)
const DISABLE_AUTO_LOGOUT = import.meta.env.VITE_DISABLE_AUTO_LOGOUT === 'true';

// ⚠️ CRITICAL: Refresh Token 동시성 제어 (Legacy API도 동일 로직 적용)
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

// 재시도 횟수 추적 (무한 루프 방지)
const retryCountMap = new Map<string, number>();
const MAX_RETRY_COUNT = 1; // 최대 1번만 재시도

const processQueue = (error: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(null);
    }
  });
  failedQueue = [];
};

export default class API {
  /**
   * API발송 메소드, /v1 + url
   */
  static async send(
    method: string,
    url: string,
    token: string | null,
    data: any,
    logout: boolean = true,
    apiurl?: string
  ) {

    try {
      const headers = this.getHeaders(token);
      const requestOptions = this.getOptions(method, headers, data);

      const res = await fetch(
        (apiurl ? apiurl : API_URL) + url,
        requestOptions as RequestInit
      )
        .then((res) => {
          return res.json();
        })
        .then(async (res) => {
          // ✅ 명확한 인증 실패 에러만 처리 (503 제외)
          const authErrors = ['EXPIRED_TOKEN', 'INVALID_TOKEN', 'UNAUTHORIZED'];

          // ⚠️ CRITICAL: 503은 서버 에러이므로 인증 에러로 처리하지 않음
          // 인증 에러만 토큰 갱신 시도
          if (authErrors.includes(res.code)) {
            // 재시도 횟수 확인 (무한 루프 방지)
            const retryKey = `${method}:${url}`;
            const currentRetryCount = retryCountMap.get(retryKey) || 0;

            if (currentRetryCount >= MAX_RETRY_COUNT) {
              console.error('[Legacy API] ❌ Max retry count reached, giving up', {
                url,
                retryCount: currentRetryCount,
              });
              retryCountMap.delete(retryKey);
              if (logout) this.forceLogout();
              return res;
            }

            // ⚠️ CRITICAL: 토큰 갱신 시도 (로그아웃 전)
            if (logout && token) {
              console.log('[Legacy API] 🔄 Token expired, attempting refresh...', {
                url,
                code: res.code,
                retryCount: currentRetryCount,
              });

              // 이미 refresh 진행 중이면 대기
              if (isRefreshing) {
                console.log('[Legacy API] ⏳ Waiting for ongoing refresh...');
                await new Promise((resolve, reject) => {
                  failedQueue.push({ resolve, reject });
                });

                // Refresh 완료 후 재시도
                const storageData = localStorage.getItem('token-storage');
                if (storageData) {
                  const { state } = JSON.parse(storageData);
                  const newToken = state?.accessToken;
                  if (newToken) {
                    console.log('[Legacy API] 🔁 Retrying with new token...');
                    retryCountMap.set(retryKey, currentRetryCount + 1);
                    return this.send(method, url, newToken, data, logout, apiurl);
                  }
                }
              }

              // Refresh 시작
              isRefreshing = true;

              try {
                const refreshSuccess = await this.attemptRefresh();

                if (refreshSuccess) {
                  processQueue(); // 대기 중인 요청들 해제

                  // 새 토큰으로 재시도
                  const storageData = localStorage.getItem('token-storage');
                  if (storageData) {
                    const { state } = JSON.parse(storageData);
                    const newToken = state?.accessToken;
                    console.log('[Legacy API] ✅ Refresh successful, retrying request');
                    retryCountMap.set(retryKey, currentRetryCount + 1);
                    return this.send(method, url, newToken, data, logout, apiurl);
                  }
                }

                // Refresh 실패 → 로그아웃
                console.error('[Legacy API] ❌ Refresh failed, logging out');
                retryCountMap.delete(retryKey);
                this.forceLogout();
              } catch (refreshError) {
                console.error('[Legacy API] ❌ Refresh error:', refreshError);
                processQueue(refreshError);
                retryCountMap.delete(retryKey);
                this.forceLogout();
              } finally {
                isRefreshing = false;
              }
            } else {
              // logout = false이거나 token이 없으면 바로 로그아웃
              if (logout) this.forceLogout();
            }
          } else if (res.code === 503 || res.code === 500 || res.code >= 500) {
            // 500/503 서버 에러: 복구 불가능한 상태이므로 로그아웃 처리
            console.error('[Legacy API] ❌ Server error, logging out', {
              url,
              code: res.code,
              message: res.message || 'Internal server error',
            });

            if (logout) {
              this.forceLogout();
            }
          }

          // 성공 시 재시도 카운트 초기화
          const retryKey = `${method}:${url}`;
          if (res.code === 0 || res.success === true) {
            retryCountMap.delete(retryKey);
          }

          return res;
        });
      return res;
    } catch (e) {
      // 네트워크 오류 등 일반 예외는 로그만 남기고 로그아웃하지 않음
      console.error('[API] Request failed:', e);
      // 인증 관련 에러가 아닌 경우 에러만 throw
      throw e;
    }
  }

  /**
   * Refresh Token으로 새 토큰 발급 시도
   */
  static async attemptRefresh(): Promise<boolean> {
    try {
      const storageData = localStorage.getItem('token-storage');
      if (!storageData) {
        console.error('[Legacy API] ❌ No token storage found');
        return false;
      }

      const { state } = JSON.parse(storageData);
      const refreshToken = state?.refreshToken;

      if (!refreshToken) {
        console.error('[Legacy API] ❌ No refresh token found');
        return false;
      }

      console.log('[Legacy API] 📡 Calling /auth/refresh...');

      // ✅ NEW_API_URL 사용 (새로운 JWT 인증 시스템)
      const response = await fetch(`${NEW_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();

      if (!response.ok || !data.data?.accessToken) {
        console.error('[Legacy API] ❌ Refresh response invalid:', data);
        return false;
      }

      const { accessToken, refreshToken: newRefreshToken, expiresIn } = data.data;

      // localStorage 업데이트
      const newState = {
        ...state,
        accessToken,
        refreshToken: newRefreshToken,
        expiresIn: expiresIn || 900,
      };

      localStorage.setItem(
        'token-storage',
        JSON.stringify({ state: newState, version: 0 })
      );

      // Zustand 동기화
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'token-storage',
        newValue: JSON.stringify({ state: newState, version: 0 }),
        url: window.location.href,
      }));

      console.log('[Legacy API] ✅ Tokens refreshed successfully');
      return true;
    } catch (error) {
      console.error('[Legacy API] ❌ Refresh failed:', error);
      return false;
    }
  }

  static async get(
    url: string,
    token: string | null,
    data: any,
    logout: boolean = true,
    apiurl?: string
  ) {
    return this.send('GET', url, token, data, logout, apiurl);
  }

  static async postNoLogout(
    url: string,
    token: string | null,
    data: any,
    apiurl?: string
  ) {
    return this.send('POST', url, token, data, false, apiurl);
  }

  static async post(
    url: string,
    token: string | null,
    data: any,
    apiurl?: string
  ) {
    return this.send('POST', url, token, data, true, apiurl);
  }

  static async delete(
    url: string,
    token: string | null,
    data: any,
    apiurl?: string
  ) {
    return this.send('DELETE', url, token, data, true, apiurl);
  }

  static async put(
    url: string,
    token: string | null,
    data: any,
    apiurl?: string
  ) {
    return this.send('PUT', url, token, data, true, apiurl);
  }

  static async patch(
    url: string,
    token: string | null,
    data: any,
    apiurl?: string
  ) {
    return this.send('PATCH', url, token, data, true, apiurl);
  }

  static async getPublic(url: string) {
    return this.send('GET', url, null, {});
  }

  static async postPublic(url: string, data: any) {
    return this.send('POST', url, null, data);
  }

  static async deletePublic(url: string, data: any) {
    return this.send('DELETE', url, null, data);
  }

  static async putPublic(url: string, data: any) {
    return this.send('PUT', url, null, data);
  }

  static async refreshToken(token: string) {
    try {
      const options = this.getOptions('POST', this.getHeaders(token), {
        token,
      });

      const res = await fetch(API_URL + '/auth/refresh', options)
        .then((res) => {
          return res.json();
        })
        .then((res) => {
          if (res.code !== 0) {
            return false;
          }
          return res;
        });
      return res;
    } catch (e) {
      console.log(e);
      this.forceLogout();
    }
  }

  static getHeaders(token: string | null) {
    const headers: any = token
      ? {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        }
      : {
          'Content-Type': 'application/json',
        };
    return headers;
  }

  static getOptions(method: string, headers: any, data?: any) {
    const options =
      method.toLocaleUpperCase() === 'GET'
        ? {
            method: method,
            headers: headers,
          }
        : {
            method: method,
            headers: headers,
            body: JSON.stringify(data),
          };
    return options;
  }

  static forceLogout() {
    if (DISABLE_AUTO_LOGOUT) {
      console.error(
        '%c[DEBUG] Auto-logout SKIPPED (Legacy API forceLogout)',
        'background: #ff6b6b; color: white; font-weight: bold; padding: 4px 8px;',
        '\n⚠️ forceLogout() was called but auto-logout is disabled',
        '\n💡 Set VITE_DISABLE_AUTO_LOGOUT=false to enable auto-logout'
      );
      return;
    }
    window.location.href = '/logout';
  }
}

export const loadData = async (number?: any) => {
  try {
    const result = await axios.get('http://api.upbit.com/v1/market/all', {
      responseType: 'json', // Ensures JSON response
    });

    const newResult = number ? result?.data?.slice(0, number) : result?.data;
    return newResult?.filter((d: any) => d.market?.match(/^KRW+/));
  } catch (error: any) {
    console.error('Error fetching data:', error.message);
    return []; // Return an empty array or handle the error as needed
  }
};
